import { TocSection } from './SettingsToc';
// Paramètres → Modèles : trames des documents générés par IA (admin).
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';
import { PDFViewer } from '@react-pdf/renderer';
import { Card } from '../ui/Card';
import { useTenantBranding } from '../../contexts/ThemeContext';
import {
  useDocumentTemplates, useCreateTemplate, useUpdateTemplate, usePdfLayouts,
} from '../../hooks/queries/useDocumentGeneration';
import type { DocumentTemplate } from '../../services/documents/documentService';
import { resolveLayoutTokens } from '../../services/documents/pdfLayoutService';
import {
  interpolateTemplate, markdownToTiptap, tiptapToPdfBlocks,
} from '../../services/documents/markdownDoc';
import { DocumentPdfDoc } from '../documents/DocumentPdf';
import { EmailTemplatesSection } from './EmailTemplatesSection';
import { PdfLayoutsSection } from './PdfLayoutsSection';

interface FormState {
  id: string | null;
  name: string;
  description: string;
  category: string;
  body_md: string;
  instructions: string;
  pdf_layout_id: string;
}
const EMPTY_FORM: FormState = { id: null, name: '', description: '', category: 'courrier', body_md: '', instructions: '', pdf_layout_id: '' };

// Dossier fictif pour l'aperçu du formulaire : les variables {{...}} de la trame
// sont remplies avec ces valeurs, les [?...] restent surlignés comme en réel.
const PREVIEW_CTX: Record<string, string> = {
  'dossier.number': 'CIME-2026-0006',
  'dossier.title': 'TechSA c/ Dupont Bâtiment',
  'dossier.juridiction': 'Tribunal judiciaire de Caen',
  'dossier.rg_number': '26/01234',
  'avocat.nom_complet': 'Julien Chatel',
  'partie.nom': 'SARL Dupont Bâtiment',
  'partie.adresse': '12 rue des Artisans, 14000 Caen',
  'convention.mode': 'forfait',
  'convention.forfait_amount': '3 000 € HT',
  'convention.hourly_rate': '250 € HT/heure',
  'date.aujourdhui': '21 juillet 2026',
};

/** Valeur de la trame, débouncée pour ne pas re-rendre le PDF à chaque frappe. */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function DocTemplatesTab() {
  const { data: templates, isLoading } = useDocumentTemplates(false);
  const { data: layouts } = usePdfLayouts();
  const { branding } = useTenantBranding();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const [form, setForm] = useState<FormState | null>(null);

  // Aperçu en direct du formulaire : trame interpolée avec le dossier fictif,
  // rendue avec la mise en page sélectionnée (ou le défaut cabinet).
  const previewMd = useDebounced(form?.body_md ?? '', 700);
  const previewDoc = useMemo(() => {
    if (!form) return null;
    const layoutRow =
      (form.pdf_layout_id ? layouts?.find((l) => l.id === form.pdf_layout_id) : undefined)
      ?? layouts?.find((l) => l.is_default);
    const ctx = { ...PREVIEW_CTX, 'cabinet.nom': branding.firm_name };
    const { text } = interpolateTemplate(previewMd, ctx);
    return {
      firmName: branding.firm_name,
      logoUrl: branding.logo_url,
      colorPrimary: branding.color_primary,
      colorGold: branding.color_gold,
      title: form.name || 'Aperçu du modèle',
      recipientLines: ['SARL Dupont Bâtiment', '12 rue des Artisans', '14000 Caen'],
      dateLine: 'Le 21 juillet 2026',
      blocks: tiptapToPdfBlocks(markdownToTiptap(text)),
      signerName: 'Julien Chatel',
      layout: resolveLayoutTokens(layoutRow),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la trame passe par previewMd (débouncée)
  }, [form?.id, form?.name, form?.pdf_layout_id, previewMd, layouts, branding]);

  const openEdit = (t: DocumentTemplate) => setForm({
    id: t.id, name: t.name, description: t.description ?? '',
    category: t.category, body_md: t.body_md, instructions: t.instructions ?? '',
    pdf_layout_id: t.pdf_layout_id ?? '',
  });

  const submit = () => {
    if (!form || !form.name.trim() || !form.body_md.trim()) {
      toast.error('Nom et trame sont obligatoires.');
      return;
    }
    const payload = {
      name: form.name.trim(), description: form.description.trim() || null,
      category: form.category.trim() || 'courrier',
      body_md: form.body_md, instructions: form.instructions.trim() || null,
      pdf_layout_id: form.pdf_layout_id || null,
    };
    const mutation = form.id
      ? updateTemplate.mutateAsync({ id: form.id, patch: payload })
      : createTemplate.mutateAsync(payload);
    mutation
      .then(() => { toast.success('Modèle enregistré.'); setForm(null); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Échec de l’enregistrement.'));
  };

  return (
    <div className="max-w-6xl space-y-4">
      <div data-toc="Documents" style={{ scrollMarginTop: 16 }} className="flex max-w-3xl items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-800">Modèles de documents</h2>
          <p className="text-sm text-neutral-500">
            Trames utilisées par l’assistant de génération. Variables : {'{{dossier.number}}'}, {'{{partie.nom}}'}, {'{{avocat.nom_complet}}'}, {'{{cabinet.nom}}'}, {'{{convention.mode}}'}, {'{{date.aujourdhui}}'}… Les passages incertains s’écrivent {'[?description]'}.
          </p>
        </div>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded bg-cime-primary px-3 py-1.5 text-sm font-medium text-white cursor-pointer"
          onClick={() => setForm(EMPTY_FORM)}
        >
          <Plus className="w-4 h-4" aria-hidden /> Nouveau modèle
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Chargement…</p>
      ) : (
        <ul className="max-w-3xl space-y-2">
          {(templates ?? []).map((t) => (
            <li key={t.id}>
              <Card>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-800">
                      {t.name}
                      {!t.is_active && <span className="ml-2 text-xs text-neutral-400">(inactif)</span>}
                    </p>
                    <p className="truncate text-xs text-neutral-500">{t.description || t.category}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-600 hover:border-neutral-300 cursor-pointer"
                      onClick={() =>
                        updateTemplate.mutate(
                          { id: t.id, patch: { is_active: !t.is_active } },
                          { onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec.') },
                        )}
                    >
                      {t.is_active ? 'Désactiver' : 'Activer'}
                    </button>
                    <button
                      type="button"
                      className="flex items-center gap-1 rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-600 hover:border-neutral-300 cursor-pointer"
                      onClick={() => openEdit(t)}
                    >
                      <Pencil className="w-3.5 h-3.5" aria-hidden /> Modifier
                    </button>
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {form && (
        <Card header={<span className="text-sm font-semibold">{form.id ? 'Modifier le modèle' : 'Nouveau modèle'}</span>}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-neutral-600">Nom *</span>
                <input className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="text-neutral-600">Catégorie</span>
                <input className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                  value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </label>
            </div>
            <label className="block text-sm">
              <span className="text-neutral-600">Description (affichée sur la carte de choix)</span>
              <input className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="text-neutral-600">Trame (markdown + variables) *</span>
              <textarea rows={12} className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 font-mono text-xs"
                value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="text-neutral-600">Consignes de rédaction pour l’IA</span>
              <textarea rows={3} className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="text-neutral-600">Mise en page</span>
              <select className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
                value={form.pdf_layout_id} onChange={(e) => setForm({ ...form, pdf_layout_id: e.target.value })}>
                <option value="">Défaut cabinet</option>
                {(layouts ?? []).map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 cursor-pointer"
                onClick={() => setForm(null)}>
                Annuler
              </button>
              <button type="button"
                className="rounded bg-cime-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 cursor-pointer"
                disabled={createTemplate.isPending || updateTemplate.isPending}
                onClick={submit}>
                Enregistrer
              </button>
            </div>
          </div>

          <div className="lg:sticky lg:top-4 lg:self-start">
            <p className="mb-1 text-xs font-medium text-neutral-500">
              Aperçu (dossier d’exemple — la mise en page sélectionnée s’applique)
            </p>
            {previewDoc && previewMd.trim() !== '' ? (
              <div className="h-[560px] overflow-hidden rounded border border-neutral-200">
                <PDFViewer width="100%" height="100%" showToolbar={false} style={{ border: 'none', minHeight: 560 }}>
                  <DocumentPdfDoc {...previewDoc} />
                </PDFViewer>
              </div>
            ) : (
              <div className="flex h-[560px] items-center justify-center rounded border border-dashed border-neutral-300 text-sm text-neutral-400">
                Écrivez la trame pour voir l’aperçu.
              </div>
            )}
          </div>
          </div>
        </Card>
      )}

      <TocSection label="Mails"><EmailTemplatesSection /></TocSection>

      <TocSection label="Mises en page PDF"><PdfLayoutsSection /></TocSection>
    </div>
  );
}
